// Geometría pura de la carta Gantt: traduce fechas ('YYYY-MM-DD') a columnas y
// anchos de barra en un grid de un día por columna, y calcula los avisos (no
// bloqueantes) que son juicio del usuario. Las validaciones que BLOQUEAN las hace
// el backend; acá solo se marca en rojo lo que conviene revisar.
//
// Las fechas son strings ISO 'YYYY-MM-DD' (el backend usa DATEONLY). Nunca se
// construye un Date a mano: para la aritmética de días se usa dayjs, y para las
// comparaciones de los avisos alcanza el orden lexicográfico del formato ISO.
import dayjs from 'libs/dayjs';

const diasEntre = (desde, hasta) => dayjs(hasta).diff(dayjs(desde), 'day');

const tieneFechas = (tarea) => Boolean(tarea?.planned_start && tarea?.planned_end);

// Si hoy cae a esta distancia (días) del rango de las tareas, el diagrama se estira
// para incluirlo; más lejos, la línea de hoy no ayuda y se omite.
const MARGEN_HOY_DIAS = 60;

/**
 * Rango de fechas que cubre el diagrama: del primer inicio planificado al último
 * fin (o a la fecha comprometida, lo que caiga más tarde). Si se pasa `hoy` y cae a
 * menos de 60 días del rango, el rango lo incluye. Devuelve el rango en ISO y la
 * cantidad de días inclusivos (columnas del grid).
 * @param {Array} tareas
 * @param {string|null} fechaComprometida
 * @param {string|null} [hoy]
 * @returns {{desde: string, hasta: string, dias: number}}
 */
export function rangoDelDiagrama(tareas, fechaComprometida, hoy = null) {
  const starts = (tareas || []).map((t) => t.planned_start).filter(Boolean);
  const ends = (tareas || []).map((t) => t.planned_end).filter(Boolean);
  const candidatosFin = [...ends];
  if (fechaComprometida) candidatosFin.push(fechaComprometida);

  let desde = starts.length ? starts.reduce((a, b) => (a < b ? a : b)) : fechaComprometida;
  let hasta = candidatosFin.length ? candidatosFin.reduce((a, b) => (a > b ? a : b)) : desde;

  if (hoy && desde && hasta) {
    if (hoy < desde && diasEntre(hoy, desde) <= MARGEN_HOY_DIAS) desde = hoy;
    else if (hoy > hasta && diasEntre(hasta, hoy) <= MARGEN_HOY_DIAS) hasta = hoy;
  }

  return { desde, hasta, dias: diasEntre(desde, hasta) + 1 };
}

/**
 * Índice de columna (0-based, un día por columna) de una fecha dentro del rango.
 * @param {string} fecha
 * @param {string} desde
 * @returns {number}
 */
export function columnaDeFecha(fecha, desde) {
  return diasEntre(desde, fecha);
}

/**
 * Posición y ancho de la barra de una tarea. `null` si le falta alguna fecha.
 * @param {{planned_start:string, planned_end:string}} tarea
 * @param {string} desde
 * @returns {{col:number, span:number}|null}
 */
export function barraDeTarea(tarea, desde) {
  if (!tieneFechas(tarea)) return null;
  const col = columnaDeFecha(tarea.planned_start, desde);
  const span = diasEntre(tarea.planned_start, tarea.planned_end) + 1;
  return { col, span };
}

/**
 * Barra derivada de una tarea con subtareas: del inicio de la primera hija (con
 * fechas) al fin de la última. Ignora las hijas sin ambas fechas. `null` si
 * ninguna hija tiene fechas.
 * @param {Array} hijas
 * @param {string} desde
 * @returns {{col:number, span:number}|null}
 */
export function barraDerivadaDelPadre(hijas, desde) {
  const conFechas = (hijas || []).filter(tieneFechas);
  if (conFechas.length === 0) return null;
  const primerInicio = conFechas.map((h) => h.planned_start).reduce((a, b) => (a < b ? a : b));
  const ultimoFin = conFechas.map((h) => h.planned_end).reduce((a, b) => (a > b ? a : b));
  return {
    col: columnaDeFecha(primerInicio, desde),
    span: diasEntre(primerInicio, ultimoFin) + 1,
  };
}

/**
 * Avisos NO bloqueantes de una tarea (juicio del usuario, se pintan en rojo pero
 * dejan trabajar):
 *   - se pasa de la fecha comprometida,
 *   - arranca antes de que termine su predecesora (según las dependencias),
 *   - no tiene fechas dentro de un Gantt encendido.
 * @param {object} tarea
 * @param {Array} tareas - todas las tareas de la ODT (para resolver predecesoras).
 * @param {Array<{id_item:number, id_item_predecesor:number}>} dependencias
 * @param {string|null} fechaComprometida
 * @returns {string[]}
 */
export function avisosDeTarea(tarea, tareas, dependencias, fechaComprometida) {
  const avisos = [];

  // Un padre no tiene fechas propias: su barra se deriva de las hijas.
  const hijas = (tareas || []).filter((t) => t.parent_id === tarea.id);
  if (hijas.length > 0) {
    const fechadas = hijas.filter(tieneFechas);
    if (fechadas.length === 0) return ['Sus subtareas no tienen fechas'];
    const ultimoFin = fechadas.map((h) => h.planned_end).reduce((a, b) => (a > b ? a : b));
    if (fechaComprometida && ultimoFin > fechaComprometida) {
      avisos.push('Termina después de la fecha comprometida');
    }
    return avisos;
  }

  if (!tieneFechas(tarea)) {
    avisos.push('Sin fechas planificadas');
    return avisos;
  }

  if (fechaComprometida && tarea.planned_end > fechaComprometida) {
    avisos.push('Termina después de la fecha comprometida');
  }

  const predecesorasIds = (dependencias || [])
    .filter((d) => d.id_item === tarea.id)
    .map((d) => d.id_item_predecesor);
  for (const idPred of predecesorasIds) {
    const pred = (tareas || []).find((t) => t.id === idPred);
    if (pred?.planned_end && tarea.planned_start < pred.planned_end) {
      avisos.push('Arranca antes de que termine su predecesora');
      break;
    }
  }

  return avisos;
}

/**
 * Meses calendario que cubre el rango (cabecera superior), con su posición y ancho
 * en columnas.
 * @param {string} desde
 * @param {number} dias
 * @returns {Array<{label:string, col:number, span:number}>}
 */
export function mesesDelRango(desde, dias) {
  const meses = [];
  for (let i = 0; i < dias; i += 1) {
    const fecha = dayjs(desde).add(i, 'day');
    const label = fecha.format('MMMM YYYY');
    const ultimo = meses[meses.length - 1];
    if (ultimo && ultimo.label === label) ultimo.span += 1;
    else meses.push({ label, col: i, span: 1 });
  }
  return meses;
}

/**
 * Columnas que caen en sábado o domingo.
 * @param {string} desde
 * @param {number} dias
 * @returns {number[]}
 */
export function finesDeSemana(desde, dias) {
  const cols = [];
  for (let i = 0; i < dias; i += 1) {
    const dow = dayjs(desde).add(i, 'day').day();
    if (dow === 0 || dow === 6) cols.push(i);
  }
  return cols;
}

/**
 * Columna de una marca (hoy, fecha comprometida) o `null` si no hay fecha o cae
 * fuera del rango.
 * @param {string|null} fecha
 * @param {string} desde
 * @param {number} dias
 * @returns {number|null}
 */
export function columnaDeMarca(fecha, desde, dias) {
  if (!fecha) return null;
  const col = diasEntre(desde, fecha);
  return col >= 0 && col < dias ? col : null;
}

/**
 * Columna a la que se centra la vista al abrir: hoy si está en el rango; si no, el
 * inicio.
 * @param {{hoy:string|null, desde:string, dias:number}} args
 * @returns {number}
 */
export function columnaInicial({ hoy, desde, dias }) {
  return columnaDeMarca(hoy, desde, dias) ?? 0;
}

/**
 * Estado visual de una barra: 'sin-fechas' (no hay barra), 'aviso' (juicio del
 * usuario, rojo), 'derivada' (padre, se calcula de las hijas) u 'ok'.
 * @param {object} tarea
 * @param {{esPadre:boolean, avisos:string[], bar:object|null}} ctx
 * @returns {'sin-fechas'|'aviso'|'derivada'|'ok'}
 */
export function estadoDeBarra(tarea, { esPadre, avisos, bar }) {
  if (!bar) return 'sin-fechas';
  if (avisos.length > 0) return 'aviso';
  if (esPadre) return 'derivada';
  return 'ok';
}

const FORMATO_ISO = 'YYYY-MM-DD';

/**
 * Fechas nuevas al arrastrar un borde de la barra. El inicio nunca pasa del fin ni el fin
 * queda antes del inicio: el mínimo es una tarea de un día.
 * @param {{planned_start:string, planned_end:string}} tarea
 * @param {'inicio'|'fin'} borde
 * @param {number} deltaDias días (con signo) que se movió el borde
 * @returns {{planned_start:string, planned_end:string}|null} `null` si no hay fechas o no cambia nada.
 */
export function fechasRedimensionadas(tarea, borde, deltaDias) {
  if (!tieneFechas(tarea)) return null;
  const { planned_start: inicio, planned_end: fin } = tarea;
  let nuevoInicio = inicio;
  let nuevoFin = fin;

  if (borde === 'inicio') {
    const candidato = dayjs(inicio).add(deltaDias, 'day').format(FORMATO_ISO);
    nuevoInicio = candidato > fin ? fin : candidato;
  } else {
    const candidato = dayjs(fin).add(deltaDias, 'day').format(FORMATO_ISO);
    nuevoFin = candidato < inicio ? inicio : candidato;
  }

  if (nuevoInicio === inicio && nuevoFin === fin) return null;
  return { planned_start: nuevoInicio, planned_end: nuevoFin };
}

/**
 * Fechas de una tarea sin fechas al soltar su barra fantasma en una columna.
 * @param {{desde:string, col:number, dias?:number}} args `dias` es la duración inicial (3).
 * @returns {{planned_start:string, planned_end:string}}
 */
export function fechasDeBarraFantasma({ desde, col, dias = 3 }) {
  const inicio = dayjs(desde).add(col, 'day');
  return {
    planned_start: inicio.format(FORMATO_ISO),
    planned_end: inicio.add(dias - 1, 'day').format(FORMATO_ISO),
  };
}

/**
 * Duración en días (inclusivos) de una tarea. Un padre dura de la primera hija fechada a
 * la última.
 * @param {object} tarea
 * @param {Array} tareas todas las tareas de la ODT (para resolver las hijas)
 * @returns {number|null} `null` si no hay fechas.
 */
export function duracionDeTarea(tarea, tareas) {
  const hijas = (tareas || []).filter((t) => t.parent_id === tarea.id);
  if (hijas.length > 0) {
    const fechadas = hijas.filter(tieneFechas);
    if (fechadas.length === 0) return null;
    const inicio = fechadas.map((h) => h.planned_start).reduce((a, b) => (a < b ? a : b));
    const fin = fechadas.map((h) => h.planned_end).reduce((a, b) => (a > b ? a : b));
    return diasEntre(inicio, fin) + 1;
  }
  if (!tieneFechas(tarea)) return null;
  return diasEntre(tarea.planned_start, tarea.planned_end) + 1;
}
